import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FeatureGate } from '@/components/feature-gate'
import { ChevronLeft, ChevronRight, FolderOpen, Eye, X } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import { triggerRevalidation } from '@/lib/utils'
import { usePages } from '@/hooks/use-pages'
import type { Tables, TablesUpdate } from '@/types/supabase'

type TenantTheme = Tables<'tenant_themes'>

// Local type for theme_presets (includes new palette_group / mode columns
// added in migration 043, before types are regenerated)
type ThemePreset = {
  id: string
  name: string
  palette_group: string | null
  mode: 'dark' | 'light' | 'any'
  sort_order: number
  color_primary: string
  color_secondary: string
  color_accent: string
  color_background: string
  color_surface: string
  color_text_primary: string
  color_text_muted: string
  color_border: string
}

const COLOR_FIELDS: { key: keyof TenantTheme; label: string }[] = [
  { key: 'color_primary', label: 'Warna Primer' },
  { key: 'color_secondary', label: 'Warna Sekunder' },
  { key: 'color_accent', label: 'Warna Aksen' },
  { key: 'color_background', label: 'Latar Belakang' },
  { key: 'color_surface', label: 'Surface' },
  { key: 'color_text_primary', label: 'Teks Utama' },
  { key: 'color_text_muted', label: 'Teks Redup' },
  { key: 'color_border', label: 'Border' },
]

const FONT_OPTIONS = [
  'Inter',
  'Roboto',
  'Poppins',
  'Montserrat',
  'Lato',
  'Open Sans',
  'Raleway',
  'Nunito',
  'DM Sans',
  'Plus Jakarta Sans',
  'Playfair Display',
  'Merriweather',
  'Lora',
  'Libre Baskerville',
  'Shippori Mincho B1',
  'Caveat',
  'Archivo Black',
]

function loadGoogleFont(family: string) {
  if (!family) return
  const id = `gfont-${family.replace(/\s+/g, '-')}`
  if (document.getElementById(id)) return
  const link = document.createElement('link')
  link.id = id
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;600;700&display=swap`
  document.head.appendChild(link)
}

function FontSelector({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (val: string) => void
}) {
  const [custom, setCustom] = useState(false)
  const selected = value || ''

  useEffect(() => {
    if (selected) loadGoogleFont(selected)
  }, [selected])

  function handleSelect(e: React.ChangeEvent<HTMLSelectElement>) {
    const val = e.target.value
    if (val === '__custom__') {
      setCustom(true)
    } else {
      setCustom(false)
      onChange(val)
    }
  }

  const isPreset = FONT_OPTIONS.includes(selected)

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium">{label}</label>

      {/* Selector */}
      <div className="flex gap-2">
        <select
          value={custom ? '__custom__' : (isPreset ? selected : (selected ? '__custom__' : ''))}
          onChange={handleSelect}
          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">-- Pilih Font --</option>
          {FONT_OPTIONS.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
          <option value="__custom__">Font kustom...</option>
        </select>
      </div>

      {/* Custom input */}
      {(custom || (!isPreset && selected)) && (
        <input
          type="text"
          value={selected}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => loadGoogleFont(e.target.value)}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="Nama font (contoh: Josefin Sans)"
        />
      )}

      {/* Preview */}
      {selected && (
        <div
          className="rounded-lg border border-border bg-muted/40 p-4 space-y-1"
          style={{ fontFamily: `'${selected}', sans-serif` }}
        >
          <p className="text-xs text-muted-foreground mb-2" style={{ fontFamily: 'inherit' }}>
            Preview — {selected}
          </p>
          <p className="text-2xl font-bold leading-tight">Idol Group</p>
          <p className="text-base font-semibold">Headline Acara Spesial</p>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Bergabunglah bersama kami dalam perjalanan yang luar biasa. Setiap momen adalah kenangan.
          </p>
          <p className="text-xs tracking-widest uppercase mt-1">ABCDEFGHIJKLMNOPQRSTUVWXYZ</p>
          <p className="text-xs">abcdefghijklmnopqrstuvwxyz 0123456789</p>
        </div>
      )}
    </div>
  )
}

// ── Preset color swatch card ──────────────────────────────────────────────────

function PresetCard({
  preset,
  applying,
  onApply,
}: {
  preset: ThemePreset
  applying: boolean
  onApply: () => void
}) {
  const swatches = [
    { color: preset.color_background, title: 'Background' },
    { color: preset.color_surface, title: 'Surface' },
    { color: preset.color_primary, title: 'Primary' },
    { color: preset.color_accent, title: 'Accent' },
    { color: preset.color_border, title: 'Border' },
  ]

  return (
    <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-3">
      {/* Color swatches */}
      <div className="flex gap-1.5">
        {swatches.map((s) => (
          <div
            key={s.title}
            title={`${s.title}: ${s.color}`}
            className="h-7 flex-1 rounded"
            style={{ backgroundColor: s.color, border: '1px solid rgba(0,0,0,0.08)' }}
          />
        ))}
      </div>

      {/* Name + text preview */}
      <div>
        <p className="text-sm font-medium leading-tight">{preset.name}</p>
        <div
          className="mt-1.5 rounded px-2 py-1.5 text-xs"
          style={{
            backgroundColor: preset.color_surface,
            color: preset.color_text_primary,
            border: `1px solid ${preset.color_border}`,
          }}
        >
          <span style={{ color: preset.color_primary, fontWeight: 600 }}>Aa</span>
          {' '}
          <span style={{ color: preset.color_text_muted }}>preview text</span>
        </div>
      </div>

      <button
        onClick={onApply}
        disabled={applying}
        className="mt-auto rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
      >
        {applying ? 'Menerapkan...' : 'Terapkan'}
      </button>
    </div>
  )
}

// ── Palette Folder Card ───────────────────────────────────────────────────────

function PaletteFolderCard({
  name,
  presets,
  onClick,
}: {
  name: string
  presets: ThemePreset[]
  onClick: () => void
}) {
  const previews = [
    ...presets.filter((p) => p.mode === 'dark'),
    ...presets.filter((p) => p.mode !== 'dark'),
  ].slice(0, 4)

  const darkCount  = presets.filter((p) => p.mode === 'dark').length
  const lightCount = presets.filter((p) => p.mode === 'light').length

  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-xl border border-border bg-card overflow-hidden text-left hover:border-primary/60 hover:shadow-md transition-all group w-full"
    >
      {/* Color strip previews */}
      <div className="h-20 bg-muted/50 p-2 flex flex-col gap-1 overflow-hidden">
        {previews.map((preset, i) => (
          <div key={i} className="flex gap-0.5 flex-1 rounded overflow-hidden">
            <div style={{ background: preset.color_background, flex: 3 }} />
            <div style={{ background: preset.color_surface,    flex: 1 }} />
            <div style={{ background: preset.color_primary,    flex: 1 }} />
            <div style={{ background: preset.color_accent,     flex: 1 }} />
            <div style={{ background: preset.color_border,     flex: 1 }} />
          </div>
        ))}
        {previews.length === 0 && (
          <div className="flex-1 flex items-center justify-center">
            <FolderOpen className="h-6 w-6 text-muted-foreground/20" />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-3 py-2.5 flex items-center gap-2">
        <FolderOpen className="h-3.5 w-3.5 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-xs truncate">{name}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {darkCount > 0 && `${darkCount} dark`}
            {darkCount > 0 && lightCount > 0 && ' · '}
            {lightCount > 0 && `${lightCount} light`}
            {darkCount === 0 && lightCount === 0 && `${presets.length} preset`}
          </p>
        </div>
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0 group-hover:text-foreground transition-colors" />
      </div>
    </button>
  )
}

// ── Preset picker tab ─────────────────────────────────────────────────────────

function PresetPicker({
  tenantId,
  onApplied,
}: {
  tenantId: string
  onApplied: () => void
}) {
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null)
  const [activeMode, setActiveMode] = useState<'dark' | 'light'>('dark')
  const [applyingId, setApplyingId] = useState<string | null>(null)

  const { data: presets = [], isLoading } = useQuery({
    queryKey: ['theme-presets-palette'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('theme_presets')
        .select(
          'id,name,palette_group,mode,sort_order,color_primary,color_secondary,color_accent,color_background,color_surface,color_text_primary,color_text_muted,color_border'
        )
        .eq('is_active', true)
        .order('sort_order')
      if (error) throw error
      return data as ThemePreset[]
    },
    staleTime: 60_000,
  })

  async function handleApply(presetId: string) {
    setApplyingId(presetId)
    try {
      const { error } = await supabase.rpc('apply_color_preset', {
        p_tenant_id: tenantId,
        p_preset_id: presetId,
      })
      if (error) throw error
      onApplied()
    } finally {
      setApplyingId(null)
    }
  }

  // Split: ungrouped flat + named groups map
  const { ungrouped, groups } = useMemo(() => {
    const ungrouped: ThemePreset[] = []
    const groupMap = new Map<string, ThemePreset[]>()
    for (const p of presets) {
      if (!p.palette_group) {
        ungrouped.push(p)
      } else {
        if (!groupMap.has(p.palette_group)) groupMap.set(p.palette_group, [])
        groupMap.get(p.palette_group)!.push(p)
      }
    }
    return { ungrouped, groups: groupMap }
  }, [presets])

  // Inside-folder presets filtered by mode
  const folderPresets = selectedGroup ? (groups.get(selectedGroup) ?? []) : null
  const folderModes   = folderPresets
    ? new Set(folderPresets.map((p) => p.mode).filter((m) => m !== 'any'))
    : null
  const showModeFilter = folderModes?.has('dark') && folderModes?.has('light')
  const visibleInFolder = folderPresets
    ? (showModeFilter
        ? folderPresets.filter((p) => !p.mode || p.mode === activeMode || p.mode === 'any')
        : folderPresets)
    : []

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Memuat preset...</p>
  }

  // ── Inside folder view ────────────────────────────────────────────────────
  if (selectedGroup !== null) {
    return (
      <div className="space-y-4">
        {/* Back + breadcrumb */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSelectedGroup(null)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Kembali
          </button>
          <span className="text-muted-foreground text-sm">/</span>
          <div className="flex items-center gap-1.5">
            <FolderOpen className="h-3.5 w-3.5 text-primary" />
            <span className="text-sm font-semibold">{selectedGroup}</span>
            <span className="text-xs text-muted-foreground">
              ({folderPresets?.length ?? 0})
            </span>
          </div>
        </div>

        {/* Mode filter */}
        {showModeFilter && (
          <div className="flex gap-1 rounded-lg border border-border bg-muted/40 p-1 w-fit">
            {(['dark', 'light'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setActiveMode(m)}
                className={[
                  'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                  activeMode === m
                    ? 'bg-card shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                ].join(' ')}
              >
                {m === 'dark' ? 'Dark Mode' : 'Light Mode'}
              </button>
            ))}
          </div>
        )}

        {/* Preset grid */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {visibleInFolder.map((preset) => (
            <PresetCard
              key={preset.id}
              preset={preset}
              applying={applyingId === preset.id}
              onApply={() => handleApply(preset.id)}
            />
          ))}
        </div>

        {visibleInFolder.length === 0 && (
          <p className="text-sm text-muted-foreground">Belum ada preset untuk mode ini.</p>
        )}
      </div>
    )
  }

  // ── Root view: folder cards + ungrouped flat ──────────────────────────────
  return (
    <div className="space-y-5">
      {/* Ungrouped presets */}
      {ungrouped.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {ungrouped.map((preset) => (
            <PresetCard
              key={preset.id}
              preset={preset}
              applying={applyingId === preset.id}
              onApply={() => handleApply(preset.id)}
            />
          ))}
        </div>
      )}

      {/* Palette group folders */}
      {groups.size > 0 && (
        <div className="space-y-2">
          {ungrouped.length > 0 && (
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
              Koleksi Palet
            </p>
          )}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {[...groups.entries()].map(([groupName, groupPresets]) => (
              <PaletteFolderCard
                key={groupName}
                name={groupName}
                presets={groupPresets}
                onClick={() => {
                  setSelectedGroup(groupName)
                  const modes = new Set(groupPresets.map((p) => p.mode).filter((m) => m !== 'any'))
                  setActiveMode(modes.has('dark') ? 'dark' : 'light')
                }}
              />
            ))}
          </div>
        </div>
      )}

      {ungrouped.length === 0 && groups.size === 0 && (
        <p className="text-sm text-muted-foreground">Belum ada preset tersedia.</p>
      )}
    </div>
  )
}

// ── Design Style Picker ───────────────────────────────────────────────────────

type DesignStyleValue = 'basic' | 'cyberpunk' | 'retro' | 'glass' | 'brutal' | 'soft'

const DESIGN_STYLES: { id: DesignStyleValue; label: string; desc: string }[] = [
  {
    id: 'basic',
    label: 'Basic',
    desc: 'Tampilan bersih dan modern. Default untuk semua tenant.',
  },
  {
    id: 'cyberpunk',
    label: 'Cyberpunk',
    desc: 'Neon glow borders, grid pattern overlay, nuansa gelap futuristik.',
  },
  {
    id: 'retro',
    label: 'Retro Editorial',
    desc: 'Tipografi serif italic, halftone dots pattern, editorial vintage.',
  },
  {
    id: 'glass',
    label: 'Glassmorphism',
    desc: 'Panel kaca buram mengambang di atas gradient mesh berwarna. Transparan, dalam, elegan.',
  },
  {
    id: 'brutal',
    label: 'Neo-Brutalism',
    desc: 'Border tebal, sudut tajam, hard shadow tanpa blur. Tegas dan kontras tinggi.',
  },
  {
    id: 'soft',
    label: 'Soft / Kawaii',
    desc: 'Card bertint pastel, heading gradient, shadow halus berwarna. Dreamy dan lembut.',
  },
]

function StylePreview({ id }: { id: DesignStyleValue }) {
  if (id === 'cyberpunk') {
    return (
      <div
        className="h-full w-full bg-gray-950 p-2 flex flex-col gap-1.5"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,229,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,255,0.06) 1px, transparent 1px)',
          backgroundSize: '12px 12px',
        }}
      >
        <div
          className="h-2 w-16 rounded-sm"
          style={{ background: '#00E5FF', boxShadow: '0 0 6px #00E5FF' }}
        />
        <div className="h-1.5 w-10 rounded-sm bg-gray-700" />
        <div
          className="mt-auto rounded border p-1.5"
          style={{
            borderColor: 'rgba(0,229,255,0.5)',
            boxShadow: '0 0 6px rgba(0,229,255,0.3)',
          }}
        >
          <div className="h-1.5 w-8 rounded-sm bg-gray-700" />
          <div className="mt-1 h-1 w-12 rounded-sm bg-gray-800" />
        </div>
      </div>
    )
  }
  if (id === 'glass') {
    return (
      <div
        className="h-full w-full p-2 flex flex-col gap-1.5"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 20% 50%, rgba(124,58,237,0.45), transparent),' +
            'radial-gradient(ellipse 60% 70% at 80% 20%, rgba(236,72,153,0.35), transparent),' +
            '#0a0a12',
        }}
      >
        <div className="h-2 w-16 rounded-sm bg-white/80" />
        <div className="h-1.5 w-10 rounded-sm bg-white/40" />
        <div
          className="mt-auto rounded-lg p-1.5"
          style={{
            background: 'rgba(255,255,255,0.08)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.18)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)',
          }}
        >
          <div className="h-1.5 w-8 rounded-sm bg-white/70" />
          <div className="mt-1 h-1 w-12 rounded-sm bg-white/40" />
        </div>
      </div>
    )
  }
  if (id === 'retro') {
    return (
      <div
        className="h-full w-full bg-gray-900 p-2 flex flex-col gap-1.5"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '5px 5px',
        }}
      >
        <div className="h-2 w-16 bg-gray-100" />
        <div className="h-1.5 w-10 bg-gray-400" />
        <div
          className="mt-auto p-1.5"
          style={{
            border: '2px solid #6b7280',
            boxShadow: '3px 3px 0 0 #6b7280',
            borderRadius: 0,
          }}
        >
          <div className="h-1.5 w-8 bg-gray-400" />
          <div className="mt-1 h-1 w-12 bg-gray-600" />
        </div>
      </div>
    )
  }
  if (id === 'brutal') {
    return (
      <div className="h-full w-full bg-gray-950 p-2 flex flex-col gap-1.5">
        <div className="h-2 w-16" style={{ background: '#e4e4e7' }} />
        <div className="h-1.5 w-10 bg-gray-600" />
        <div
          className="mt-auto p-1.5"
          style={{
            border: '2px solid #e4e4e7',
            boxShadow: '4px 4px 0 0 #7C3AED',
            borderRadius: 0,
          }}
        >
          <div className="h-1.5 w-8 bg-gray-400" />
          <div className="mt-1 h-1 w-12 bg-gray-600" />
        </div>
      </div>
    )
  }
  if (id === 'soft') {
    return (
      <div className="h-full w-full bg-gray-950 p-2 flex flex-col gap-1.5">
        <div
          className="h-2 w-16 rounded-sm"
          style={{
            background: 'linear-gradient(135deg, #7C3AED, #EC4899)',
            WebkitBackgroundClip: 'text',
          }}
        />
        <div className="h-1.5 w-10 rounded-sm bg-gray-600" />
        <div
          className="mt-auto rounded-xl p-1.5"
          style={{
            background: 'color-mix(in srgb, #7C3AED 10%, #1A1A2E)',
            border: '1px solid color-mix(in srgb, #7C3AED 30%, transparent)',
            boxShadow: '0 4px 16px color-mix(in srgb, #7C3AED 15%, transparent)',
          }}
        >
          <div
            className="h-1.5 w-14 rounded-sm"
            style={{ background: 'linear-gradient(135deg, #7C3AED, #EC4899)' }}
          />
          <div className="mt-1 h-1 w-10 rounded-sm bg-gray-600" />
        </div>
      </div>
    )
  }
  // basic
  return (
    <div className="h-full w-full bg-gray-900 p-2 flex flex-col gap-1.5">
      <div className="h-2 w-16 rounded-sm bg-violet-500" />
      <div className="h-1.5 w-10 rounded-sm bg-gray-600" />
      <div className="mt-auto rounded-lg border border-gray-700 bg-gray-800 p-1.5">
        <div className="h-1.5 w-8 rounded-sm bg-gray-600" />
        <div className="mt-1 h-1 w-12 rounded-sm bg-gray-700" />
      </div>
    </div>
  )
}

function DesignStylePicker({
  value,
  saving,
  onSelect,
  onPreview,
}: {
  value: DesignStyleValue
  saving: boolean
  onSelect: (v: DesignStyleValue) => void
  onPreview: (v: DesignStyleValue) => void
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {DESIGN_STYLES.map((style) => {
        const isActive = value === style.id
        return (
          <div
            key={style.id}
            role="button"
            tabIndex={0}
            onClick={() => !isActive && !saving && onSelect(style.id)}
            onKeyDown={(e) =>
              (e.key === 'Enter' || e.key === ' ') && !isActive && !saving && onSelect(style.id)
            }
            className={[
              'relative rounded-xl border-2 p-4 text-left transition-all outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isActive
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:border-primary/40 hover:bg-muted/30',
              saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            ].join(' ')}
          >
            <div className="mb-3 h-20 w-full rounded-lg overflow-hidden">
              <StylePreview id={style.id} />
            </div>
            <p className="text-sm font-semibold">{style.label}</p>
            <p className="mt-0.5 text-xs text-muted-foreground leading-snug">{style.desc}</p>

            {/* Preview button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onPreview(style.id)
              }}
              className="mt-3 flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors"
            >
              <Eye className="h-3 w-3" />
              Preview
            </button>

            {isActive && (
              <div className="absolute top-2.5 right-2.5 h-4 w-4 rounded-full bg-primary flex items-center justify-center">
                <svg
                  className="h-2.5 w-2.5 text-primary-foreground"
                  viewBox="0 0 10 10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1.5 5L4 7.5L8.5 2.5" />
                </svg>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Design Style Preview Modal ────────────────────────────────────────────────

function StylePreviewModal({
  style,
  tenantSlug,
  publicBase,
  onClose,
}: {
  style: DesignStyleValue
  tenantSlug: string
  publicBase: string
  onClose: () => void
}) {
  const [currentPage, setCurrentPage] = useState<string>('home')
  const { data: pages = [] } = usePages()
  const styleLabel = DESIGN_STYLES.find((s) => s.id === style)?.label ?? style

  const iframeSrc =
    currentPage === 'home'
      ? `${publicBase}/${tenantSlug}/preview?design_style=${style}&full=1`
      : `${publicBase}/${tenantSlug}/preview?design_style=${style}&page_slug=${currentPage}&full=1`

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      <div className="flex h-12 shrink-0 items-center gap-3 border-b border-border px-4">
        <p className="text-sm font-semibold shrink-0">
          Preview — <span className="text-primary">{styleLabel}</span>
        </p>

        {/* Page selector */}
        <select
          value={currentPage}
          onChange={(e) => setCurrentPage(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="home">Homepage</option>
          {pages.map((page) => (
            <option key={page.slug} value={page.slug}>
              {page.title}
            </option>
          ))}
        </select>

        <span className="ml-auto hidden text-xs text-muted-foreground sm:inline">
          ESC untuk tutup
        </span>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1.5 hover:bg-muted/50 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-hidden">
        <iframe
          key={iframeSrc}
          src={iframeSrc}
          className="h-full w-full border-0"
          title={`Preview gaya ${styleLabel} — ${currentPage}`}
        />
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

type Tab = 'preset' | 'warna' | 'tipografi' | 'gaya'

export default function ThemeSettingsPage() {
  const tenantId   = useAuthStore((s) => s.currentTenant?.id)
  const tenantSlug = useAuthStore((s) => s.currentTenant?.slug)
  const qc = useQueryClient()

  const publicBase =
    (import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined) ??
    `https://${tenantSlug}.oneidol.id`
  const [form, setForm] = useState<Partial<TenantTheme>>({})
  const [saved, setSaved] = useState(false)
  const [activeTab, setActiveTab] = useState<Tab>('preset')
  const [previewStyle, setPreviewStyle] = useState<DesignStyleValue | null>(null)

  const { data: theme, isLoading } = useQuery({
    queryKey: ['tenant-theme', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenant_themes')
        .select('*')
        .eq('tenant_id', tenantId!)
        .single()
      if (error) throw error
      return data as TenantTheme
    },
  })

  useEffect(() => {
    if (theme) {
      setForm(theme)
      if (theme.font_family_heading) loadGoogleFont(theme.font_family_heading)
      if (theme.font_family_body) loadGoogleFont(theme.font_family_body)
    }
  }, [theme])

  const updateTheme = useMutation({
    mutationFn: async (updates: TablesUpdate<'tenant_themes'>) => {
      const { data, error } = await supabase
        .from('tenant_themes')
        .update(updates)
        .eq('tenant_id', tenantId!)
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['tenant-theme', tenantId] })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
      await triggerRevalidation('tenant-theme')
    },
  })

  if (isLoading || !theme) {
    return <p className="text-muted-foreground">Memuat tema...</p>
  }

  function handleSave() {
    const { id, tenant_id, created_at, updated_at, ...updates } = form as TenantTheme
    updateTheme.mutate(updates)
  }

  function handlePresetApplied() {
    qc.invalidateQueries({ queryKey: ['tenant-theme', tenantId] })
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
    triggerRevalidation('tenant-theme')
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'preset',    label: 'Preset Warna' },
    { id: 'warna',     label: 'Palet Warna' },
    { id: 'tipografi', label: 'Tipografi' },
    { id: 'gaya',      label: 'Gaya Tampilan' },
  ]

  return (
    <FeatureGate feature="custom_theme">
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Tema Visual</h1>
        <p className="text-muted-foreground">
          Kustomisasi warna dan tipografi halaman publik idol group Anda.
        </p>
      </div>

      {/* Tab Nav */}
      <div className="flex border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={[
              'px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            ].join(' ')}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Preset Warna */}
      {activeTab === 'preset' && tenantId && (
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="mb-5">
            <h2 className="text-sm font-semibold">Pilih Preset</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Terapkan langsung ke tampilan publik. Warna di tab "Palet Warna" akan ikut terupdate.
            </p>
          </div>
          <PresetPicker tenantId={tenantId} onApplied={handlePresetApplied} />
        </div>
      )}

      {/* Tab: Warna */}
      {activeTab === 'warna' && (
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {COLOR_FIELDS.map(({ key, label }) => (
              <div key={key} className="space-y-1">
                <label className="text-sm font-medium">{label}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={(form[key] as string) ?? '#000000'}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    className="h-8 w-10 cursor-pointer rounded border border-input"
                  />
                  <input
                    type="text"
                    value={(form[key] as string) ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="#000000"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab: Tipografi */}
      {activeTab === 'tipografi' && (
        <div className="rounded-xl border border-border bg-card p-6 space-y-6">
          <FontSelector
            label="Font Heading"
            value={(form.font_family_heading as string) ?? ''}
            onChange={(val) => setForm((f) => ({ ...f, font_family_heading: val }))}
          />
          <hr className="border-border" />
          <FontSelector
            label="Font Body"
            value={(form.font_family_body as string) ?? ''}
            onChange={(val) => setForm((f) => ({ ...f, font_family_body: val }))}
          />
          <hr className="border-border" />
          <FontSelector
            label="Font Dekorasi"
            value={((form as Record<string, unknown>).font_family_decorative as string) ?? ''}
            onChange={(val) => setForm((f) => ({ ...f, font_family_decorative: val }))}
          />
        </div>
      )}

      {/* Tab: Gaya Tampilan */}
      {activeTab === 'gaya' && (
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <div className="mb-5">
            <h2 className="text-sm font-semibold">Pilih Gaya Tampilan</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Menentukan karakteristik visual keseluruhan halaman publik.
              Dikombinasikan dengan palet warna yang sudah dipilih.
            </p>
          </div>
          <DesignStylePicker
            value={((form as Record<string, unknown>).design_style as DesignStyleValue) ?? 'basic'}
            saving={updateTheme.isPending}
            onSelect={(val) => {
              setForm((f) => ({ ...f, design_style: val }))
              updateTheme.mutate({ design_style: val } as TablesUpdate<'tenant_themes'>)
            }}
            onPreview={(val) => setPreviewStyle(val)}
          />
        </div>
      )}

      {/* Save row — only shown for manual tabs */}
      {(activeTab === 'warna' || activeTab === 'tipografi') && (
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={updateTheme.isPending}
            className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {updateTheme.isPending ? 'Menyimpan...' : 'Simpan Perubahan'}
          </button>
          {saved && <p className="text-sm text-green-600">Tema berhasil disimpan!</p>}
        </div>
      )}

      {/* Success feedback for preset apply */}
      {activeTab === 'preset' && saved && (
        <p className="text-sm text-green-600">Preset berhasil diterapkan!</p>
      )}

      {/* Success feedback for design style auto-save */}
      {activeTab === 'gaya' && saved && (
        <p className="text-sm text-green-600">Gaya tampilan berhasil disimpan!</p>
      )}
    </div>

    {/* Design style preview modal */}
    {previewStyle !== null && tenantSlug && (
      <StylePreviewModal
        style={previewStyle}
        tenantSlug={tenantSlug}
        publicBase={publicBase}
        onClose={() => setPreviewStyle(null)}
      />
    )}
    </FeatureGate>
  )
}
